import React, { useMemo, useState, useEffect } from 'react';
import {
  ThemeProvider,
  createTheme,
  responsiveFontSizes,
  alpha,
  lighten,
  darken,
} from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import CssBaseline from '@mui/material/CssBaseline';

import { ThemeModeContext } from '../hooks/ThemeMode';
import { buildTablePalette } from './palette/table';
import { buildHeaderPalette } from './palette/header';
import { buildCardInfoPalette } from './palette/Surface/cardInfo';
import { buildCardInfoContentPalette } from './palette/Surface/cardInfoContent';
import { buildCardInfoContentHorizontalPalette } from './palette/Surface/cardInfoContentHorizontal';
import { buildAccordionPalette } from './palette/Surface/accordion';

const THEME_STORAGE_KEY = 'app-theme-mode';
const THEME_SYNC_EVENT = 'app:theme-mode';

function normalizeMode(value, fallback) {
  if (value === 'dark' || value === 'light') return value;
  return fallback;
}

export default function ThemeModeProvider({ children }) {
  const prefersDark = typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
  const preferredMode = prefersDark ? 'dark' : 'light';

  const [mode, setMode] = useState(() => {
    const stored = typeof window !== 'undefined' ? window.localStorage?.getItem(THEME_STORAGE_KEY) : null;
    return normalizeMode(stored, preferredMode);
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage?.setItem(THEME_STORAGE_KEY, mode);
      document.documentElement.setAttribute('data-theme', mode);
      document.body?.setAttribute?.('data-theme', mode);
    }
  }, [mode]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const onStorage = (event) => {
      if (event.key !== THEME_STORAGE_KEY) return;
      const nextMode = normalizeMode(event.newValue, preferredMode);
      setMode((prev) => (prev === nextMode ? prev : nextMode));
    };

    const onThemeSync = (event) => {
      const nextMode = normalizeMode(event?.detail?.mode, preferredMode);
      setMode((prev) => (prev === nextMode ? prev : nextMode));
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(THEME_SYNC_EVENT, onThemeSync);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(THEME_SYNC_EVENT, onThemeSync);
    };
  }, [preferredMode]);

  const toggleMode = () => {
    setMode((prev) => {
      const nextMode = prev === 'light' ? 'dark' : 'light';
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(THEME_SYNC_EVENT, { detail: { mode: nextMode } }));
      }
      return nextMode;
    });
  };

  const theme = useMemo(() => {
    const lightBrand = '#800000';
    const darkBrand = '#008080';
    const brandMain = mode === 'dark' ? darkBrand : lightBrand;
    const darkBackground = '#080b12';
    const darkPaper = '#111624';
    const darkElevated = '#1b2132';
    const darkBorder = 'rgba(255,255,255,0.12)';
    const lightBorder = '#eadcdc';

    const palette = {
      mode,
      common: {
        white: '#ffffff',
        black: '#000000',
      },
      grey,
      primary: {
        light: '#4e575b',
        main: '#222d32',
        dark: '#171f23',
        contrastText: '#fff',
      },
      secondary: {
        light: lighten(brandMain, mode === 'dark' ? 0.05 : 0.2),
        main: brandMain,
        dark: darken(brandMain, 0.2),
        contrastText: '#fff',
      },
      info: {
        main: mode === 'dark' ? lighten(darkBrand, 0.05) : '#1d4ed8',
        contrastText: '#fff',
      },
      success: {
        main: mode === 'dark' ? '#34d399' : '#00a65a',
        contrastText: '#fff',
      },
      error: {
        main: mode === 'dark' ? '#f87171' : '#dd4b39',
        contrastText: '#fff',
      },
      warning: {
        light: '#b28704',
        main: '#ffc107',
        dark: '#ffcd38',
        contrastText: '#fff',
      },
      background: {
        default: mode === 'dark' ? darkBackground : '#f5f5f5',
        paper: mode === 'dark' ? darkPaper : '#ffffff',
      },
      text: {
        primary: mode === 'dark' ? '#f5f5f5' : '#1f1f1f',
        secondary: mode === 'dark' ? '#c5c6c9' : '#5f5f5f',
      },
      divider: mode === 'dark' ? darkBorder : '#dcdcdc',
    };

    const surface = {
      default: palette.background.default,
      paper: palette.background.paper,
      elevated: mode === 'dark' ? darkElevated : '#ffffff',
      muted: mode === 'dark' ? alpha('#ffffff', 0.04) : '#f7f7f7',
      subtle: mode === 'dark' ? darken(darkPaper, 0.08) : '#f4f6f8',
      border: mode === 'dark' ? darkBorder : lightBorder,
      hover: mode === 'dark' ? alpha('#ffffff', 0.08) : alpha('#000000', 0.04),
    };

    palette.surface = surface;
    palette.textField = {
      bg: mode === 'dark' ? darken(darkPaper, 0.08) : '#ffffff',
      text: palette.text.primary,
      border: mode === 'dark' ? darkBorder : lightBorder,
      focusBorder: mode === 'dark' ? darkBrand : brandMain,
      label: palette.text.secondary,
      disabledBg: mode === 'dark' ? alpha('#ffffff', 0.08) : '#f0f0f0',
      disabledText: palette.text.disabled,
      error: palette.error?.main,
    };
    palette.select = {
      bg: palette.textField.bg,
      text: palette.text.primary,
      border: palette.textField.border,
      icon: palette.text.secondary,
      label: palette.text.secondary,
      labelFocused: palette.textField.focusBorder,
      openBg: mode === 'dark' ? darkElevated : '#ffffff',
      openText: palette.text.primary,
      selectRowBg: mode === 'dark' ? alpha(brandMain, 0.18) : alpha(brandMain, 0.12),
      selectRowText: palette.text.primary,
      hoverBg: surface.hover,
    };
    const linkMain = mode === 'dark' ? lighten(darkBrand, 0.25) : '#1d4ed8';
    palette.link = {
      main: linkMain,
      hover: mode === 'dark' ? lighten(darkBrand, 0.4) : darken(linkMain, 0.18),
      visited: mode === 'dark' ? lighten(darkBrand, 0.15) : darken(linkMain, 0.28),
    };
    const menuBaseBg = '#0f1d2a';
    const menuBasePanelBg = '#0f1d2a';
    const menuBorder = 'rgba(255,255,255,0.18)';
    const darkTone = lighten(darkBrand, 0.28);
    const lightTone = lightBrand;
    const lightTextTone = '#ffffff';

    palette.menu = mode === 'dark'
      ? {
          bg: menuBaseBg,
          text: darkTone,
          icon: darkTone,
          panelBg: menuBasePanelBg,
          panelText: darkTone,
          border: menuBorder,
          buttonBg: alpha(darkTone, 0.12),
          buttonText: darkTone,
          buttonHoverBg: alpha(darkTone, 0.22),
          buttonHoverText: darkTone,
          buttonActiveBg: alpha(darkTone, 0.28),
          buttonActiveText: '#ffffff',
          itemHoverBg: alpha(darkTone, 0.1),
          itemActiveBg: alpha(darkTone, 0.24),
          itemActiveText: darkTone,
          badgeBg: '#fb923c',
          badgeText: '#1f2937',
        }
      : {
          bg: menuBaseBg,
          text: lightTextTone,
          icon: lightTone,
          panelBg: menuBasePanelBg,
          panelText: lightTextTone,
          border: menuBorder,
          buttonBg: alpha('#ffffff', 0.1),
          buttonText: lightTextTone,
          buttonHoverBg: alpha('#ffffff', 0.18),
          buttonHoverText: lightTextTone,
          buttonActiveBg: alpha('#ffffff', 0.24),
          buttonActiveText: '#ffffff',
          itemHoverBg: alpha('#ffffff', 0.1),
          itemActiveBg: alpha('#ffffff', 0.18),
          itemActiveText: lightTextTone,
          badgeBg: '#ea580c',
          badgeText: '#ffffff',
        };

    const paletteWithContrast = createTheme({ palette }).palette;
    palette.getContrastText = paletteWithContrast.getContrastText;

    palette.table = buildTablePalette({
      mode,
      brandMain,
      palette,
      darkElevated,
    });
    palette.header = buildHeaderPalette({
      mode,
      brandMain,
    });
    palette.cardInfo = buildCardInfoPalette({
      mode,
      brandMain,
      palette,
    });
    palette.cardInfoContent = buildCardInfoContentPalette({
      mode,
      brandMain,
      palette,
    });
    palette.cardInfoContentHorizontal = buildCardInfoContentHorizontalPalette({
      mode,
      brandMain,
      palette,
    });
    palette.accordion = buildAccordionPalette({
      mode,
      brandMain,
      palette,
      darkElevated,
    });

    const baseTheme = createTheme({
      palette,
      zIndex: {
        // Regla global: todo el app por debajo del menú externo (100).
        mobileStepper: 10,
        fab: 80,
        speedDial: 80,
        appBar: 99,
        drawer: 90,
        modal: 92,
        snackbar: 94,
        tooltip: 96,
      },
      typography: {
        // Accessible, professional default stack (uses Inter if available, falls back safely).
        htmlFontSize: 16,
        fontFamily:
          'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        fontSize: 14,
        h1: { fontWeight: 700, lineHeight: 1.15, fontSize: 34 },
        h2: { fontWeight: 700, lineHeight: 1.18, fontSize: 28 },
        h3: { fontWeight: 700, lineHeight: 1.22, fontSize: 24 },
        h4: { fontWeight: 700, lineHeight: 1.25, fontSize: 20 },
        h5: { fontWeight: 700, lineHeight: 1.28, fontSize: 18 },
        h6: { fontWeight: 700, lineHeight: 1.3, fontSize: 16 },
        subtitle1: { fontWeight: 600, lineHeight: 1.35, fontSize: 14 },
        subtitle2: { fontWeight: 600, lineHeight: 1.35, fontSize: 13 },
        body1: { fontWeight: 400, lineHeight: 1.5, fontSize: 14 },
        body2: { fontWeight: 400, lineHeight: 1.45, fontSize: 13 },
        caption: { fontWeight: 400, lineHeight: 1.35, fontSize: 12 },
        overline: { fontWeight: 600, lineHeight: 1.25, fontSize: 11, letterSpacing: '0.08em' },
        button: { fontWeight: 600, lineHeight: 1.2, fontSize: 13, textTransform: 'none' },
      },
      components: {
        MuiTypography: {
          defaultProps: {
            // Enforce semantic HTML tags for heading variants across all views.
            variantMapping: {
              h1: 'h1',
              h2: 'h2',
              h3: 'h3',
              h4: 'h4',
              h5: 'h5',
              h6: 'h6',
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundColor: palette.background.paper,
              color: palette.text.primary,
              transition: 'background 0.2s ease, color 0.2s ease, border-color 0.2s ease',
            },
            outlined: {
              borderColor: mode === 'dark' ? darkBorder : lightBorder,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: mode === 'dark' ? darkElevated : '#fff',
              borderColor: mode === 'dark' ? darkBorder : lightBorder,
              color: palette.text.primary,
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            head: {
              backgroundColor: mode === 'dark' ? darkBrand : brandMain,
              color: '#fff',
              fontWeight: 600,
            },
            body: {
              borderColor: mode === 'dark' ? darkBorder : '#f0f0f0',
            },
            root: {
              borderColor: mode === 'dark' ? darkBorder : '#f0f0f0',
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              '&.MuiTableRow-hover:hover': {
                backgroundColor: mode === 'dark' ? alpha('#ffffff', 0.04) : '#fafafa',
              },
            },
          },
        },
        MuiInputBase: {
          styleOverrides: {
            root: {
              backgroundColor: mode === 'dark' ? darken(darkPaper, 0.1) : '#fff',
              borderRadius: 8,
            },
            input: {
              color: palette.text.primary,
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              borderColor: mode === 'dark' ? darkBorder : '#d0d0d0',
            },
            root: {
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: mode === 'dark' ? '#fff' : brandMain,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: mode === 'dark' ? darkBrand : brandMain,
                boxShadow: `0 0 0 1px ${mode === 'dark' ? alpha(darkBrand, 0.5) : alpha(brandMain, 0.3)}`,
              },
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            icon: {
              color: palette.text.primary,
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: palette.text.secondary,
              '&.Mui-focused': {
                color: palette.textField.focusBorder || palette.primary.main,
              },
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: {
              color: palette.text.secondary,
            },
          },
        },
        MuiLink: {
          defaultProps: {
            underline: 'hover',
          },
          styleOverrides: {
            root: {
              color: palette.link.main,
              textDecorationColor: alpha(palette.link.main, 0.5),
              '&.MuiLink-colorPrimary, &.MuiLink-colorSecondary': {
                color: palette.link.main,
              },
              '&:hover': {
                color: palette.link.hover,
                textDecorationColor: alpha(palette.link.hover, 0.7),
              },
              '&:visited': {
                color: palette.link.visited,
              },
            },
          },
        },
        MuiTabs: {
          styleOverrides: {
            indicator: {
              // Avoid using palette.primary.main because here "primary" is structural (dark) in both modes.
              // Use brand accent for selected-state visibility in dark mode.
              backgroundColor: palette.secondary.main,
              height: 3,
              borderRadius: 9999,
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              color: palette.text.secondary,
              '&.Mui-selected': {
                color: palette.text.primary,
                fontWeight: 700,
              },
              '&.Mui-focusVisible': {
                backgroundColor: alpha(palette.secondary.main, mode === 'dark' ? 0.22 : 0.12),
              },
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            // Fix contrast in dark mode for default outlined buttons (color=primary).
            outlinedPrimary: {
              color: palette.text.primary,
              borderColor: alpha(palette.text.primary, mode === 'dark' ? 0.35 : 0.28),
              '&:hover': {
                borderColor: alpha(palette.text.primary, mode === 'dark' ? 0.55 : 0.4),
                backgroundColor: alpha(palette.text.primary, mode === 'dark' ? 0.08 : 0.04),
              },
            },
          },
        },
        MuiAccordion: {
          styleOverrides: {
            root: {
              // Keep accordions neutral; use brand only as subtle accent (not full headers).
              backgroundColor: palette.background.paper,
              color: palette.text.primary,
              border: `1px solid ${palette.divider}`,
              borderRadius: 'var(--radius-8)',
              boxShadow: 'none',
              overflow: 'hidden',
              '&:before': {
                display: 'none',
              },
              '&.Mui-expanded': {
                margin: 0,
              },
            },
          },
        },
        MuiAccordionSummary: {
          styleOverrides: {
            root: {
              minHeight: 44,
              paddingLeft: 16,
              paddingRight: 16,
              color: palette.text.primary,
              transition: 'background 0.15s ease, color 0.15s ease',
              // Ensure summary icon buttons don't fall back to MUI's "action.active" grey in light mode.
              '& .MuiIconButton-root': {
                color: 'inherit',
              },
              '&:hover': {
                backgroundColor: palette.surface?.hover || alpha(palette.text.primary, mode === 'dark' ? 0.08 : 0.04),
              },
              '&.Mui-expanded': {
                minHeight: 44,
                // Subtle "selected" hint on expanded state without painting the header in brand color.
                backgroundColor: alpha(palette.secondary.main, mode === 'dark' ? 0.14 : 0.08),
              },
              '&.Mui-focusVisible': {
                backgroundColor: alpha(palette.secondary.main, mode === 'dark' ? 0.22 : 0.12),
              },
            },
            content: {
              margin: '10px 0',
              '&.Mui-expanded': {
                margin: '10px 0',
              },
            },
            expandIconWrapper: {
              color: 'inherit',
              opacity: mode === 'dark' ? 0.85 : 0.75,
              '&.Mui-expanded': {
                opacity: 1,
              },
            },
          },
        },
        MuiAccordionDetails: {
          styleOverrides: {
            root: {
              padding: 16,
              borderTop: `1px solid ${alpha(palette.divider, mode === 'dark' ? 0.9 : 1)}`,
              backgroundColor: palette.surface?.subtle || palette.background.default,
            },
          },
        },
        MuiCssBaseline: {
          styleOverrides: {
            ':root': {
              '--link-color': palette.link.main,
              '--link-color-hover': palette.link.hover,
              '--link-color-visited': palette.link.visited,
            },
            html: {
              fontSize: '16px',
              WebkitTextSizeAdjust: '100%',
              MozTextSizeAdjust: '100%',
              textSizeAdjust: '100%',
            },
            body: {
              backgroundColor: 'var(--bg)',
              color: 'var(--fg)',
              fontFamily:
                'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              fontSize: 'var(--fs-body, 14px)',
              lineHeight: 'var(--lh-body, 1.5)',
              textRendering: 'optimizeLegibility',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
            },
            'h1, h2, h3, h4, h5, h6': {
              margin: 0,
              fontFamily:
                'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
              color: 'inherit',
            },
            '@media (max-width:600px)': {
              body: {
                fontSize: 'var(--fs-body, 15px)',
              },
            },
            ':focus-visible': {
              outline: `2px solid ${alpha(palette.link.main, mode === 'dark' ? 0.9 : 0.8)}`,
              outlineOffset: 2,
            },
            '.wrapper, .content-wrapper, .content': {
              backgroundColor: 'var(--bg)',
              color: 'var(--fg)',
            },
            '.content-header': {
              backgroundColor: 'transparent',
              color: 'var(--fg)',
            },
            '.content-header h1, .content-header small': {
              color: 'var(--fg)',
            },
            main: {
              backgroundColor: 'var(--bg)',
              color: 'var(--fg)',
              minHeight: '100%',
            },
            a: {
              color: 'var(--link-color)',
            },
            'a:hover': {
              color: 'var(--link-color-hover)',
            },
            'a:visited': {
              color: 'var(--link-color-visited)',
            },
            '.link-user, .link-mt-order': {
              color: 'var(--link-color)',
              fontWeight: 600,
            },
            '.link-user:hover, .link-mt-order:hover': {
              color: 'var(--link-color-hover)',
            },
            'main .panel, main .panel-default': {
              backgroundColor: 'var(--panel)',
              color: 'var(--fg)',
              borderColor: 'var(--border)',
              borderRadius: 8,
              boxShadow: 'var(--shadow-soft)',
            },
            'main .panel-heading': {
              backgroundColor: 'var(--panel-2)',
              color: 'var(--fg)',
              borderBottom: '1px solid var(--line)',
            },
            'main .panel-body': {
              color: 'var(--fg)',
            },
            'main .panel-footer': {
              backgroundColor: 'var(--panel-2)',
              color: 'var(--fg)',
              borderTop: '1px solid var(--line)',
            },
            'main .btn': {
              borderRadius: 6,
              borderColor: 'var(--border)',
              backgroundColor: 'var(--panel-2)',
              color: 'var(--fg)',
            },
            'main .btn-default': {
              backgroundColor: 'var(--panel-2)',
              color: 'var(--fg)',
              borderColor: 'var(--border)',
            },
            'main .btn-primary': {
              backgroundColor: 'var(--accent)',
              color: 'var(--on-accent)',
              borderColor: 'var(--accent)',
            },
            'main .btn-primary:hover': {
              backgroundColor: 'var(--accent-strong)',
              borderColor: 'var(--accent-strong)',
            },
            'main .btn-success': {
              backgroundColor: 'var(--success)',
              borderColor: 'var(--success)',
              color: 'var(--on-accent)',
            },
            'main .btn-warning': {
              backgroundColor: 'var(--warning)',
              borderColor: 'var(--warning)',
              color: 'var(--on-accent)',
            },
            'main .btn-danger': {
              backgroundColor: 'var(--error)',
              borderColor: 'var(--error)',
              color: 'var(--on-accent)',
            },
            'main .btn-info': {
              backgroundColor: 'var(--info)',
              borderColor: 'var(--info)',
              color: 'var(--on-accent)',
            },
            'main .label, main .badge': {
              backgroundColor: 'var(--panel-alt)',
              color: 'var(--fg)',
              border: '1px solid var(--border)',
            },
            'main .label-default, main .badge-default': {
              backgroundColor: 'var(--panel-alt)',
              color: 'var(--fg)',
            },
            'main .label-primary, main .badge-primary': {
              backgroundColor: 'var(--accent)',
              color: 'var(--on-accent)',
            },
            'main .label-success, main .badge-success': {
              backgroundColor: 'var(--success)',
              color: 'var(--on-accent)',
            },
            'main .label-warning, main .badge-warning': {
              backgroundColor: 'var(--warning)',
              color: 'var(--on-accent)',
            },
            'main .label-danger, main .badge-danger': {
              backgroundColor: 'var(--error)',
              color: 'var(--on-accent)',
            },
            'main .text-muted': {
              color: 'var(--muted) !important',
            },
            'main .text-primary': {
              color: 'var(--accent) !important',
            },
            'main .text-success': {
              color: 'var(--success) !important',
            },
            'main .text-warning': {
              color: 'var(--warning) !important',
            },
            'main .text-danger': {
              color: 'var(--error) !important',
            },
            'main .text-info': {
              color: 'var(--info) !important',
            },
            'main .form-control': {
              backgroundColor: 'var(--panel)',
              color: 'var(--fg)',
              borderColor: 'var(--border)',
              boxShadow: 'none',
            },
            'main .form-control:focus': {
              borderColor: 'var(--accent)',
              boxShadow: '0 0 0 1px var(--accent)',
            },
            'main .input-group-addon': {
              backgroundColor: 'var(--panel-alt)',
              color: 'var(--fg)',
              borderColor: 'var(--border)',
            },
            'main .table': {
              color: 'var(--fg)',
            },
            'main .table > thead > tr > th': {
              backgroundColor: 'var(--panel-2)',
              color: 'var(--fg)',
              borderColor: 'var(--line)',
            },
            'main .table > tbody > tr > td, main .table > tbody > tr > th': {
              borderColor: 'var(--line)',
            },
            'main .table-striped > tbody > tr:nth-of-type(odd)': {
              backgroundColor: 'var(--row-odd)',
            },
            'main .table-striped > tbody > tr:nth-of-type(even)': {
              backgroundColor: 'var(--row-even)',
            },
            'main .table-hover > tbody > tr:hover': {
              backgroundColor: 'var(--row-hover)',
            },
            'main .modal-content': {
              backgroundColor: 'var(--panel)',
              color: 'var(--fg)',
              borderColor: 'var(--border)',
            },
            'main .modal-header': {
              borderBottom: '1px solid var(--line)',
            },
            'main .modal-footer': {
              borderTop: '1px solid var(--line)',
              backgroundColor: 'var(--panel-2)',
            },
            'main .dropdown-menu': {
              backgroundColor: 'var(--panel)',
              borderColor: 'var(--border)',
              color: 'var(--fg)',
            },
            'main .dropdown-menu > li > a': {
              color: 'var(--fg)',
            },
            'main .dropdown-menu > li > a:hover': {
              backgroundColor: 'var(--row-hover)',
            },
            'main .nav-tabs': {
              borderBottom: '1px solid var(--line)',
            },
            'main .nav-tabs > li > a': {
              color: 'var(--fg)',
            },
            'main .nav-tabs > li.active > a, main .nav-tabs > li.active > a:hover, main .nav-tabs > li.active > a:focus': {
              backgroundColor: 'var(--panel)',
              color: 'var(--fg)',
              borderColor: 'var(--line)',
            },
          },
        },
        MuiButton: {
          defaultProps: {
            disableElevation: true,
          },
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 8,
              minHeight: 36,
            },
            containedSecondary: {
              backgroundColor: palette.secondary.main,
              color: palette.getContrastText(palette.secondary.main),
              '&:hover': {
                backgroundColor: mode === 'dark'
                  ? lighten(palette.secondary.main, 0.1)
                  : darken(palette.secondary.main, 0.1),
              },
            },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              backgroundColor: palette.background.paper,
            },
          },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              backgroundColor: mode === 'dark' ? palette.grey[200] : palette.grey[900],
              color: mode === 'dark' ? palette.grey[900] : palette.grey[50],
            },
          },
        },
      },
    });

    // Responsive typography helps across desktop/mobile without per-view tuning.
    return responsiveFontSizes(baseTheme, { factor: 2.2 });
  }, [mode]);

  const contextValue = useMemo(
    () => ({
      mode,
      toggleMode,
    }),
    [mode],
  );

  return (
    <ThemeModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
